import React from 'react';
import {
  ListItem,
  ListItemText,
  createStyles,
  makeStyles,
  Typography,
  IconButton,
  Collapse,
} from '@bb-ui/react-library';
import { ExecutionDetailResponseBody, ExecutionLogEntry } from '@lct-jobs/core';
import ReactJson from 'react-json-view';
import moment from 'moment';
import { ChevronDown as ExpandMore, ChevronUp as ExpandLess } from '@bb-ui/icons/dist/small';
import { JobCardList } from './basic/JobCardList';
import { useTranslation } from 'react-i18next';
import { isReleaseStageExecution } from '../isReleaseStageExecution';

const useStyles = makeStyles((theme) =>
  createStyles({
    scrollableContainer: {
      maxHeight: 600,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    logItem: {
      paddingTop: 8,
      paddingBottom: 8,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
      alignItems: 'flex-start',
      '& + &': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },
    levelBadge: {
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      minWidth: 48,
      display: 'inline-block',
    },
    message: {
      fontSize: '13px',
      lineHeight: 1.4,
      wordBreak: 'break-word' as const,
    },
    timestamp: {
      fontSize: '11px',
      color: 'rgba(0, 0, 0, 0.4)',
    },
    expandButton: {
      padding: 4,
      marginLeft: 4,
    },
    jsonContainer: {
      padding: '4px 16px 8px',
      borderTop: `1px solid ${theme.palette.divider}`,
      background: 'rgba(0, 0, 0, 0.02)',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
  }),
);

const JobLogItem: React.FC<{ entry: ExecutionLogEntry; index: number }> = (props) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);
  const logTime = moment(props.entry.timestamp);
  const hasData = !!props.entry.data;

  return (
    <>
      <ListItem className={classes.logItem}>
        <ListItemText
          disableTypography
          primary={
            <div className={classes.headerRow}>
              <span className={classes.levelBadge}>{props.entry.level}</span>
              <Typography className={classes.message} variant="body2" component="span">
                {props.entry.message}
              </Typography>
              {hasData && (
                <IconButton
                  className={classes.expandButton}
                  onClick={() => setExpanded(!expanded)}
                  size="small"
                >
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
            </div>
          }
          secondary={<span className={classes.timestamp}>{logTime.format('lll')}</span>}
        />
      </ListItem>
      {hasData && (
        <Collapse in={expanded}>
          <div className={classes.jsonContainer}>
            <ReactJson
              src={props.entry.data as object}
              enableClipboard={false}
              name={false}
              displayDataTypes={false}
              collapsed={1}
            />
          </div>
        </Collapse>
      )}
    </>
  );
};

export interface JobLogsProps {
  jobExecution?: ExecutionDetailResponseBody;
  hasMore?: boolean;
}

export const JobLogsBox: React.FC<JobLogsProps> = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const execution = props.jobExecution;
  const rawLogs = execution?.logs;
  // SNS is unordered; sort chronologically by event timestamp. An invalid timestamp sorts last
  // instead of making the comparator return NaN.
  const logs = React.useMemo(() => {
    const timeOf = (entry: ExecutionLogEntry) => {
      const time = new Date(entry.timestamp).getTime();
      return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
    };
    const sorted = [...(rawLogs ?? [])].sort((a, b) => {
      const timeA = timeOf(a);
      const timeB = timeOf(b);
      if (timeA === timeB) return 0;
      return timeA < timeB ? -1 : 1;
    });
    // Keys follow the entry rather than its position, so expanded state stays on the same entry
    // when an older one arrives. An occurrence suffix separates identical entries; `data` is left
    // out of the key to keep it cheap on every poll.
    const seen = new Map<string, number>();
    return sorted.map((entry) => {
      const base = `${entry.timestamp}|${entry.level}|${entry.message}`;
      const occurrence = seen.get(base) ?? 0;
      seen.set(base, occurrence + 1);
      return { entry, key: `${base}#${occurrence}` };
    });
  }, [rawLogs]);
  const releaseStageExecution = isReleaseStageExecution(execution);
  let subtitle: string | undefined;

  if (releaseStageExecution) {
    subtitle = t('jobService.parentChildFailuresDescription', { count: logs.length });
  } else if (logs.length > 0) {
    subtitle = `${logs.length} entries`;
  }

  return (
    <JobCardList
      title={releaseStageExecution ? t('jobService.parentChildFailuresTitle') : 'Execution Logs'}
      subtitle={subtitle}
    >
      <div className={classes.scrollableContainer}>
        {logs.map(({ entry, key }, index) => (
          <JobLogItem key={key} entry={entry} index={index} />
        ))}
      </div>
    </JobCardList>
  );
};
